import { createElement, type MouseEvent } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import {
  AvatarGroup,
  Flex,
  FlexColumn,
  LabelLink,
  OverflowTooltip,
  StatusChip,
  SvgIcon,
} from '@elementary/common-ui/components';
import { getTestIcon } from '@elementary/common-ui/components/cellRenderers/testNameRenderer';
import { getAssetIcon } from '@elementary/common-ui/utils/assets';
import { ReactComponent as CalendarIcon } from '@elementary/common-ui/assets/16x16/clock-countdown.svg';
import { ReactComponent as ColumnIcon } from '@elementary/common-ui/assets/columns.svg';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import CopyButton from 'shared/components/CopyButton';
import { getMonitorChipStatus } from '../../statusUtils';
import type { Monitor } from '../../types';

const Card = styled(FlexColumn)<{ $clickable: boolean; $isResolved: boolean }>`
  width: 100%;
  gap: 8px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme }) => theme.palette.common.gray17};
  border-radius: 4px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  opacity: ${({ $isResolved }) => ($isResolved ? 0.72 : 1)};
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    ${({ $clickable, theme }) =>
      $clickable &&
      `
        border-color: ${theme.palette.common.gray5};
        background: ${theme.palette.common.gray3};
      `}
  }
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const AssetBreadcrumb = styled(Flex)`
  align-items: center;
  gap: 8px;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  min-width: 0;
`;

const PropertiesRow = styled(Flex)`
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PropertyItem = styled(Flex)`
  align-items: center;
  gap: 6px;
  padding: 5px 6px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray17};
  border-radius: 3px;
`;

const TimeInfo = styled(Flex)`
  align-items: center;
  gap: 6px;
  padding: 3px 6px;
  height: 26px;
`;

const DescriptionText = styled(Typography)`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.gray4};
`;

const PropertiesLabel = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray4};
  width: 60px;
`;

type Props = {
  monitor: Monitor;
};

const IncidentMonitorCard = ({ monitor }: Props) => {
  const { openSidePanel } = useSidePanelParams();
  const { test, latestTestExecution, asset, signal } = monitor;

  const testName = test?.name ?? 'Unknown monitor';
  const testType = test?.type ?? '';
  const subType = latestTestExecution?.subType ?? '';
  const subTestUniqueId = latestTestExecution?.subTestUniqueId ?? '';
  const assetFqn = asset?.fqn ?? asset?.name ?? '';
  const columnName = latestTestExecution?.columnName ?? '';
  const owners = test?.owners ?? [];
  const ownerItems = owners
    .slice(0, 3)
    .map((owner) => (typeof owner === 'string' ? owner : String(owner)));
  const isResolved = !signal.isActive;
  const signalTimeLabel =
    isResolved && signal.endTime
      ? `Resolved ${localizeDate(signal.endTime).fromNow()}`
      : isResolved
      ? 'Resolved'
      : localizeDate(signal.lastFailureTime).fromNow();

  const handleAssetClick = (event?: MouseEvent) => {
    event?.stopPropagation();
    if (asset?.id) {
      openSidePanel('asset', asset.id, undefined);
    }
  };

  const handleTestClick = (event?: MouseEvent) => {
    event?.stopPropagation();
    if (subTestUniqueId) {
      openSidePanel('test', subTestUniqueId, undefined);
    }
  };

  const handleCardClick = subTestUniqueId
    ? () => handleTestClick()
    : asset?.id
    ? () => handleAssetClick()
    : undefined;

  return (
    <Card
      $clickable={Boolean(handleCardClick)}
      $isResolved={isResolved}
      onClick={handleCardClick}
    >
      <Flex gap={1.5}>
        {test && (
          <HeaderRow>
            {createElement(getTestIcon(testType, subType) || (() => null))}
            <LabelLink onClick={handleTestClick}>{testName}</LabelLink>
          </HeaderRow>
        )}

        {assetFqn && asset && (
          <AssetBreadcrumb>
            <SvgIcon style={{ flexShrink: 0 }} width={17}>
              {getAssetIcon(asset)}
            </SvgIcon>
            <OverflowTooltip tooltipContent={assetFqn}>
              {asset?.id ? (
                <LabelLink onClick={handleAssetClick}>{assetFqn}</LabelLink>
              ) : (
                <Typography sx={{ fontSize: '14px' }}>{assetFqn}</Typography>
              )}
            </OverflowTooltip>
            <CopyButton
              textToCopy={asset.name || assetFqn}
              sx={{ height: '20px', width: '20px', flexShrink: 0 }}
            />
          </AssetBreadcrumb>
        )}
        {columnName && (
          <AssetBreadcrumb>
            <SvgIcon style={{ flexShrink: 0 }} width={17}>
              <ColumnIcon />
            </SvgIcon>
            <OverflowTooltip tooltipContent={columnName}>
              <Typography sx={{ fontSize: '14px' }}>{columnName}</Typography>
            </OverflowTooltip>
          </AssetBreadcrumb>
        )}
      </Flex>

      <FlexColumn gap={1}>
        <PropertiesRow>
          <PropertiesLabel>Properties</PropertiesLabel>

          {ownerItems.length > 0 && (
            <PropertyItem>
              <AvatarGroup items={ownerItems} size="small" />
            </PropertyItem>
          )}

          <StatusChip status={getMonitorChipStatus(signal)} />

          <TimeInfo>
            <CalendarIcon />
            <Typography sx={{ fontSize: '13px' }}>{signalTimeLabel}</Typography>
          </TimeInfo>
        </PropertiesRow>
      </FlexColumn>

      {test?.description && (
        <DescriptionText>{test.description}</DescriptionText>
      )}
    </Card>
  );
};

export default IncidentMonitorCard;
